import Util from './util';
import ShopifyStore from './shopify-store';
import ShopifyCart from './shopify-cart';
import AjaxWatch from './ajax-watch';
import Request from './request';

import * as log from 'loglevel';
import Translate from './translate';
import ProductOptions from './product-options';
import ConditionsEngine from './conditions-engine';
import {CartItemProperties, currencyConvert, DiscountType, ThemeConfig, ThemeCurrency, AppExtensions} from './const';
import {formatMoney} from './money';
import CartOptionsPrice from './cart-options-price';
import Order from './order/order';
import CartSave from './config/cart-save';
import SavedConfigs from './config/saved-configs';

import {render} from 'lit-html';
import discountCodeTemplate from './template/discount-code.template';
import bundleCartTemplate from './template/bundle-cart.template';
import {cdnImg} from './shopify-cdn';


class OptionsApp {
    constructor(config) {

        this.config = config;

        // all active ProductOptions
        this.products = [];

        let cartOptions = {};
        // NEW: check if the current locale is in the url > append the locale prefix to the cart api (returns the item urls with the locale in the url)
        if(Translate.locale && String.prototype.startsWith && window.location.pathname.toLowerCase().startsWith('/' + Translate.locale.toLowerCase())){
            cartOptions.api = '/' + Translate.locale.toLowerCase();
        }
        // NEW: shopify markets global vars for routes & locale
        if(window.Shopify && window.Shopify.routes && window.Shopify.routes.root && window.Shopify.routes.root !== '/'){
            let root = window.Shopify.routes.root;
            // cart api no leading slash
            cartOptions.api = root[root.length - 1] === '/' ? root.substr(0, root.length - 1) : root;
        }

        /**
         * @type {ShopifyStore}
         */
        this.shopifyStore = ShopifyStore.getInstance({ api: cartOptions.api });  // NEW: also load product with api prefix (required to get the right currency and value)

        /**
         * @type {ShopifyCart}
         */
        this.shopifyCart = ShopifyCart.getInstance(cartOptions);

        this.conditionsEngine = new ConditionsEngine();

        // store reference to support update form outside
        this.currency = ThemeCurrency;

        // map theme config to global ThemeConfig
        if(this.config.theme){
            for (let property in this.config.theme) {
                if(this.config.theme.hasOwnProperty(property)){
                    ThemeConfig[property] = this.config.theme[property];
                }
            }
        }

        // NEW: theme specific config - check if the current theme matches (first check if theme id's are provided)
        if(this.config.themeId && (window.Shopify && window.Shopify.theme && window.Shopify.theme.id)){
            if(this.config.themeId !== window.Shopify.theme.id.toString()){
                log.debug('--> theme.id config does not match - reseting custom selectors ', this.config.themeId, window.Shopify.theme.id.toString());
                delete this.config.previewImgMainSelector;
                delete this.config.previewImgThumbnailSelector;
                delete this.config.itemImgParentSelector;
            }
        }

        // NEW: auto theme settings (move to backend in the future)
        if(this.shopifyStore.isTheme('Dawn')){
            ThemeConfig.themeGallery = typeof this.config.theme.themeGallery !== 'undefined' ? this.config.theme.themeGallery : true; // auto select current theme preview (to support variant changes)
            ThemeConfig.productPriceObserver = true; // requires productPriceObserver > auto enable
            this.config.itemImgParentSelector = '.cart-notification, .cart-item'; // only auto update cart item images that are child of one of the provided selectors
        } else if(this.shopifyStore.isTheme('Horizon')){
            // auto select current theme preview (only needed for non-variant images / variant changes already reload the gallery)
            ThemeConfig.themeGallery = typeof this.config.theme.themeGallery !== 'undefined' ? this.config.theme.themeGallery : true;
            ThemeConfig.productPriceObserver = true; // not strictly required (variant change trigger new price finding) but works better with observer
            ThemeConfig.previewHiddenExclude = true; // required for live preview mobile support
            ThemeConfig.productFormValidityPatch = true; // theme gates its add-to-cart animation on form.checkValidity() (assets/product-form.js handleClick)
        }
        // themes from: https://archetypethemes.co/   have price setting like 9.'00' where decimals are separate element
        if(window.theme && window.theme.settings && window.theme.settings.superScriptPrice){
            ThemeConfig.amountNoDecimals = true
        }

        // map to global ThemeCurrency
        // IMPORTANT: execute before setup() to also update when used in backend editor
        // INFO: some moneyFormats contain escaped html
        // INFO: MultiCurrency will overwrite the formats again (only executed in setup)
        for(let format in this.config.currencyFormats){
            if(this.config.currencyFormats.hasOwnProperty(format)){
                let currencyFormat = this.config.currencyFormats[format];
                // simple check if contains escaped html entities
                if(currencyFormat.indexOf('&') !== -1) {
                    // decode to render as html
                    currencyFormat = Util.decodeHtml(currencyFormat);
                }

                // name mapping form GraphQL to Shop theme variable (better to use shop theme syntax for compatibility)
                if(format === 'moneyWithCurrencyFormat'){
                    format = 'moneyFormatWithCurrency';
                }

                ThemeCurrency[format] = currencyFormat;
            }
        }

        // NEW: priceVariant value mapped from config (required for currency conversion with separate product)
        ThemeCurrency.priceVariant = !this.config.priceDraft;
        ThemeCurrency.priceVariantId = this.config.priceVariantId;
        ThemeCurrency.priceVariantValue = this.config.priceVariantValue;
        ThemeCurrency.priceProductHandle = this.config.priceProductHandle;

        // NEW: Shopify Markets multi currency support: dynamic currency (used for add-ons)  // NEW: auto enable/disable based on active currencies (set in app block) : FALLBACK to server setting
        ThemeCurrency.currencyConvert = (window.theme && Array.isArray(window.theme.currencies)) ? window.theme.currencies.length > 1 : this.config.currencyConvert;

        if(window.Shopify && window.Shopify.currency) {
            ThemeCurrency.currency = window.Shopify.currency.active;

            if(ThemeCurrency.currencyConvert){
                // rate is automatically set in shopify theme
                let currencyFactor = parseFloat(window.Shopify.currency.rate);
                // check if conversion required
                if(!isNaN(currencyFactor) && currencyFactor !== 1){
                    ThemeCurrency.currencyFactor = currencyFactor;
                }
            }
        }

        // LEGACY: 3rd party currency conversion apps (use html in moneyFormat) AND currency conversion is NOT enabled > keep the currencyFormat with the html to avoid breaking current setups
        if(!ThemeCurrency.currencyConvert && ThemeCurrency.moneyFormat.indexOf('<') !== -1 && ThemeCurrency.moneyFormat.indexOf('>') !== -1){
            ThemeConfig.currencyFormat = false;
        }

        // NEW: Shopify Markets multi currency support: current selected moneyFormat set via app-embed.liquid block
        if(ThemeConfig.currencyFormat && window.theme && window.theme.moneyFormat){
            ThemeCurrency.moneyFormat = window.theme.moneyFormat;
            ThemeCurrency.moneyFormatWithCurrency = window.theme.moneyFormatWithCurrency || window.theme.moneyFormat; //  some themes have no moneyFormatWithCurrency (not yet using app-embed)
        }

        // NEW: show prices only for logged in customers && setup check (avoid checking on admin backend)
        if(ThemeConfig.priceVisibleCustomer && this.config.setup){
            ThemeConfig.priceVisible = this.shopifyStore.getCustomerId() !== null;
        }

        // NEW: map checkout extensibility config (used for price hash generation)
        if(this.config.checkout && this.config.checkoutConfig){
            ThemeCurrency.priceVariant = false; // disable extra product added to the cart with checkout config
            ThemeCurrency.checkout = this.config.checkoutConfig;
        }

        // NEW: app extensions
        if(config.extensions){
            let enabledExtensions = config.extensions.split(",");
            for (let i = 0; i < enabledExtensions.length; i++) {
                const extension = enabledExtensions[i];
                // enable extension via name
                AppExtensions[extension.toUpperCase()] = true;
            }
        }

        // NEW: custom JS/CSS (executed before init of options and on ALL pages)
        if(this.config.css){
            Util.addStyleElement(this.config.css);
        }
        if(this.config.js){
            Util.addScriptElement(this.config.js);
        }

        // setup checks all upload configs and auto initialized if the conditions match
        if(this.config.setup){

            // NEW: check if document is fully loaded (required to find all products on the store)
            if (/complete|interactive|loaded/.test(document.readyState)) {
                // In case the document has finished parsing, document's readyState will
                // be one of "complete", "interactive" or (non-standard) "loaded".
                this.setup();
            }
            // rare case where script is executed before document is parsed (manual script loading in head & all assets are cached)
            else {
                log.debug('---> document not ready (' + document.readyState + ') => waiting for DOMContentLoaded');
                // The document is not ready yet, so wait for the DOMContentLoaded event
                document.addEventListener('DOMContentLoaded', () => this.setup(), false);
            }

            // trigger new setup if we are in theme editor
            this.shopifyStore.onThemeSectionEvent('load',(e) => {
                // any sectionId with 'product' in it is changed  ( NEW: trigger always re-setup, there can be sections without product in id )
                //if(e.detail.sectionId.indexOf('product') !== -1){
                    this.setup();
               // }
            });

            // NEW: order restore logic (from customer or admin backend)
            // a product page with an order line (buy again per line on the product page): the product options open the line, no cart restore
            if(Util.hasQueryParam(window.location.href, Order.RESTORE_PARAM) && this.shopifyStore.isProduct() && Util.hasQueryParam(window.location.href, Order.LINE_PARAM)){
                log.debug('order line opened by the product options');
            }
            else if(Util.hasQueryParam(window.location.href, Order.RESTORE_PARAM)){
                let order = new Order({api: this.config.api, shop: this.config.shop, app: 'options' });

                order.restoreOrder(Util.getQueryParam(window.location.href, Order.RESTORE_PARAM));
            }
            // NEW: recovery link outside a product page (the cart) - a product page applies it on its product options
            else if(Util.hasQueryParam(window.location.href, SavedConfigs.recoverName) && !this.shopifyStore.isProduct()){
                let order = new Order({api: this.config.api, assets: this.config.assets, shop: this.config.shop, app: 'options' });

                order.restoreConfigs(Util.getQueryParam(window.location.href, SavedConfigs.recoverName));
            }
        }
    }

    setup(resetProducts = false){

        // single page apps that re-init the app can still have previous product in memory
        if(resetProducts){
            ShopifyStore.products = {};
        }

        // destroy previous configs
        this.destroy();

        // product detail testing - without this no product is passed to the product-options
        //this.shopifyStore.fakeProduct('earth');
        // parse html elements with ProductJson already serialized ( avoids additional ajax request )
        this.shopifyStore.parseProductJsonElements();

        // fetch product via api and return all found products as map with handle as key
        this.shopifyStore.tryGetProductMap().then((productMap) => {

            // check if config matches the current product
            for (let i = 0; i < this.config.configs.length; i++) {
                const optionsConfigData = this.config.configs[i];

                log.debug('-> evaluating options-config:', optionsConfigData.uuid); // description is not returned

                // 0 = show on a single product
                if(optionsConfigData.targetMode === 0){

                    // eval product only condition for each found product
                    let conditions = [{
                        data: 'product',
                        field: 'product',
                        products: [optionsConfigData.product] // single product target stored on config
                    }];

                    for (let handle in productMap) {

                        if(productMap.hasOwnProperty(handle)){
                            let product = productMap[handle];

                            // evaluate with single product condition
                            this._evaluateProductTargeting(optionsConfigData, product, conditions, 1);
                        }

                    }
                }
                // multiple products
                else {
                    // 0 = show on all products
                    if(optionsConfigData.conditionMode === 0){

                        // find the main product ( IS REQUIRED )
                        if(this.shopifyStore.isProduct()){
                            let product = productMap[this.shopifyStore.getHandleFromUrl() + '.js'];

                            if(product){
                                // evaluate without conditions to always match
                                this._evaluateProductTargeting(optionsConfigData, product, [], 0);
                            }
                        }

                    } else {

                        // for conditions we need a product
                        if(optionsConfigData.conditions.length > 0){

                            // eval condition for each found product
                            for (let handle in productMap) {

                                if(productMap.hasOwnProperty(handle)){
                                    let product = productMap[handle];

                                    // evaluate with the configured conditions & mode for multiple product match support
                                    this._evaluateProductTargeting(optionsConfigData, product, optionsConfigData.conditions, optionsConfigData.conditionMode);
                                }

                            }

                        }
                    }
                }


            }

            // NEW: theme editor app embed / block - show setup info IF no active product options
            if (this.products.length === 0) {
                if(this.shopifyStore.isThemeEditor() && !this.config.lazy){ // NEW: exclude lazy config mode - configs can still be evaluated after this check
                    this._evaluateThemeEditor(productMap);
                }
            }
            // NEW: check incompatible apps - ONLY show if a product was setup - (better to let the customer first set it up to avoid direct uninstall)
            else{
                // wait for scripts to be added from other apps before checking
                setTimeout(() => {
                    this._compatible();
                }, 1000);
            }

        });

        // !setup = (app backend)  && only if options config are configured (most cases if the app is used)
        if(this.config.setup && this.config.configs.length > 0){

            // NEW: only register watch once (e.g .setup called again from outside - or quick view)
            if(!this._watch && this.config.watch !== false){ // test mode set watch to false (avoid cart fetch)
                this._watchCartItems();

                // inject option properties into ajax request
                if(ThemeConfig.productAjaxInject){
                    this._watchAjaxAdd();
                }

                // log full checkout details for debugging
                if(ThemeConfig.checkoutLog) {
                    this._watchFormSubmit();
                }

                if(ThemeConfig.productQuickView){
                    this._watchQuickView();
                }

                // HACKY: custom app integrations support via cloudlift.submit event
                this._watchExternalSubmit();

                this._watch = true;
            }

        }


    }

    destroy(){
        // destroy all active product options
        while(this.products.length){
            const productOptions = this.products.pop();
            productOptions.destroy();
        }

        if(this._ajaxInjectUnregister){
            this._ajaxInjectUnregister();
            delete this._ajaxInjectUnregister;
        }
    }

    async _evaluateProductTargeting(optionsConfigData, product, conditions, conditionMode){

        // set product to evaluate conditions
        this.conditionsEngine.data.product = product;

        let productMatch = this.conditionsEngine.evaluateConditions(conditions, conditionMode, 'config.uuid=' + optionsConfigData.uuid);

        if(productMatch){

            // NEW: lazy config needs to fetch to full config content (before only uuid + conditions were fetched)
            if(this.config.lazy){
                //  IMPORTANT: check if a currently a config is being lazy fetched to keep the ordering (required to merge based on sort order)
                //  this could be avoided by making the full product targeting eval async await based - but we use this hack instead to avoid to much refactoring
                if(this._lazyFetch){
                    await this._lazyFetch;
                }

                // store current promise
                this._lazyFetch = this._getConfigLazy(optionsConfigData);

                // wait for config to be fetched
                await this._lazyFetch;
            }

            // specific form selector ( required for multiple products on single page support )
            // this id is generated by {% form 'product', product %} ( not available in all themes ! )
            let formSelector = '#product_form_' + product.id;
            //  NEW: alternative selector if theme overwrites form id - for featured product support
            formSelector += ', [data-product-form="' + product.id + '"]';

            productMatch = this._createProductOptions(optionsConfigData, product, formSelector);
            // general product form selector if not found
            if(!productMatch){
                productMatch = this._createProductOptions(optionsConfigData, product);
            }

            if(!productMatch){
                log.error('no container found options-config:', optionsConfigData.uuid, product);
            }
        }

        log.debug('--> evaluated options-config:', optionsConfigData.uuid, product, productMatch);
    }

    async _getConfigLazy(optionsConfigData){

        // in cloud we use assets url to cache lazy config fetch   SAME LOGIC: app-options.js
        const api = this.config.api.indexOf('local') === -1 ? this.config.api.replace('api.','assets.') : this.config.api;

        // fetch the app config with the options config uuid
        const response = await new Request(api + '/api/assets/options.json', {
            method: 'GET',
            responseType: 'json',
            queryParams: {shop: this.config.shop, config: optionsConfigData.uuid }
        });

        if(response.data?.configs){
            let appConfig = response.data;
            // find config by uuid (should always be returned)
            for (let i = 0; i < appConfig.configs.length; i++) {
                const config = appConfig.configs[i];
                if(config.uuid === optionsConfigData.uuid){
                    // merge config into optionsConfigData
                    Object.assign(optionsConfigData, config);
                    log.debug('--> lazy fetch options-config:', optionsConfigData.uuid, optionsConfigData);
                    // merge lists to global config
                    if(appConfig.lists){
                        this.config.lists = this.config.lists || {}; // init if not set
                        Object.assign(this.config.lists, appConfig.lists);
                    }
                    return;
                }
            }
        }

        log.warn('[live-options] lazy config missing:', optionsConfigData.uuid);
    }

    _evaluateThemeEditor(productMap) {

        // check if theme editor blocks are not disabled (manually closed)
        const cookieName = 'cl_live_theme';
        let blockEnabled = Util.readCookie(cookieName);

        if(blockEnabled === '0'){
            return;
        }

        for (let handle in productMap) {

            if(productMap.hasOwnProperty(handle)){
                let product = productMap[handle];

                // attach to form OR app block
                let container = this._findOrCreateContainer(null, product, 'form[action*="/cart/add"]:not([id*=instal])');

                if(container){
                    let isNew = this.config.configs.length === 0; // no configs setup yet
                    let isAppBlock = container.dataset.liveOptions !== undefined; // data-live-options is set by app block

                    container.classList.add('cl-block');
                    let content = `<style>
                            .cl-block { padding: 10px;border-radius: 3px;background-color: rgb(55, 167, 220, 1); border: 2px solid #000;margin-bottom: 10px;opacity: 0.75; position: relative; }
                            .cl-block span { font-size:14px; display: block; margin-bottom:5px; }
                            .cl-block a, .cl-block span { color:#fff; }
                            .cl-block a.live { font-size:20px;font-weight:bold;line-height: 1;text-shadow: 1px 1px 1px black;text-decoration: none; }
                            .cl-block a.link { font-weight:bold;text-shadow: 1px 1px 1px black; }
                            .cl-block .live-close { position: absolute; right:7px; top:0px; font-size:20px; cursor:pointer; }
                    </style>
                    <span class="live-close">✅</span>
                    <span><a class="live" href="/admin/apps/live-product-options">Live Product Options</a></span>
                    <span>Custom product options will appear here. ${!isNew ? '<b>(only shown in editor)</b>': ''}</span>`;

                    if(isNew){
                        content += '<span><b>NEXT:</b> save the theme & continue with the setup in the app.</span>';
                    } else {
                        if(!isAppBlock) {
                            content += '<span>To change the location visit: <a class="link" href="https://docs.cloudlift.app/article/126-product-options-block-in-theme-editor" target="_blank">Product Options App Block</a></span>'
                        }
                    }

                    container.innerHTML = content;

                    container.querySelector('.live-close').addEventListener('click', (e) => {
                        // remove
                        container.parentElement.removeChild(container);
                        // add cookie to not show again next time
                        Util.writeCookie(cookieName, '0', 30, '/');
                    });

                    // complete
                    return;
                }
            }

        }
    }

    _createProductOptions(optionsConfigData, product, formSelector){

        // NEW: extend options if already initialized on the same products (required to support price add-ons and validation)
        // INFO: this only supports extending options (LIVE preview is excluded and CSS is not loaded - because the root uuid is different)
        for (let i = 0; i < this.products.length; i++) {
            const productOptions = this.products[i];
            if(productOptions.settings.product.id === product.id){

                log.debug('-> MERGE', optionsConfigData.uuid ,'options with existing productOptions for product.id: ', product.id, optionsConfigData.options);

                const mergeOptions = () => {

                    // generate ids for the new options    NEW: skip personalize init
                    productOptions._generateOptionIds(optionsConfigData.options, 'option_' + product.id + '_', false, productOptions.config.options.length);

                    // HACKY: translation is only called on init - we have to call it for the merged options
                    if(productOptions._translate){
                        productOptions._translate.config = optionsConfigData; // overwrite options with only the new - the already translated options have circular references because of the controllers
                        productOptions._translate.translate();
                    }

                    // INFO: we append all the options at the end
                    // we could call productOptions.updateOptions BUT the logic (values/controllers) is not required and to complex for this use case
                    productOptions.config.options.push(...optionsConfigData.options);

                    // NEW: prefill URL params on the merged options - _prefillFromUrl only ran on the PRIMARY config during _init (before merge),
                    // so merged options would otherwise never receive their URL values (only first matching config was filled)
                    // INFO: 'item' (cart item restore) takes priority and is skipped here (matches _init logic)
                    if(productOptions._prefillFromUrl
                        && !Util.hasQueryParam(window.location.href, 'item')
                        && Util.hasQueryParam(window.location.href, '_f')){
                        productOptions._prefillFromUrl(optionsConfigData.options, false); // skip render: (currency-aware) handles rendering the prefilled values
                    }

                    /* OVERKILL: add if customer DEMAND, new UI params for pAutoExclude = Exclude all options from other configs / pxExclude = Exclude specific options
                       INFO: currently personalize option only supported on the PRIMARY config and MERGED options are not included in the personalize option
                    let personalizeOption;
                    for (let j = 0; j < optionsConfigData.options.length; j++) {
                        if(optionsConfigData.options[j].type === 'personalize'){
                            personalizeOption = optionsConfigData.options[j];
                            // NEW: auto exclude all currently shown options from the personalize option of the merged config
                            if(personalizeOption.params.pAutoExclude){
                                let optionNames = productOptions.config.options.map((option) => option.name).join(',');
                                personalizeOption.params.pExclude = personalizeOption.params.pExclude ? personalizeOption.params.pExclude + ',' + optionNames : optionNames;
                            }
                        }
                    }

                    // NEW: check for personalize option - already initialized on PRIMARY config
                    if(productOptions._personalizeOption && !productOptions._personalizeOption.pAutoExclude){ // skip if auto exclude is enabled (personalize is kept standalone)
                        // remove all personalize options from the merged config (only one personalize option is allowed)
                        optionsConfigData.options = optionsConfigData.options.filter(o => o.type !== 'personalize');
                        // find group_personalize option (fixed name) and append to the existing personalize option
                        for (let j = 0; j < productOptions.config.options.length; j++) {
                            let productOption = productOptions.config.options[j];
                            if(productOption.name === 'group_personalize'){
                                productOption.options.push(...optionsConfigData.options);
                                break;
                            }
                        }
                    } else {
                        // INFO: we append all the options at the end
                        // we could call productOptions.updateOptions BUT the logic (values/controllers) is not required and to complex for this use case
                        productOptions.config.options.push(...optionsConfigData.options);
                    }

                    // MERGED config personalize init
                    if(personalizeOption){
                        productOptions._initPersonalize(personalizeOption);
                    } */
                    // NEW: if add-ons are merged the stock check setting needs to be set to lookup the add-on stock + price in currency
                    if(ThemeConfig.productLinkAvailable
                        // NEW: bundle products always requires that we fetch the accurate price AND editorMode needs to fetch the products as well to get variant info for price/img
                        || AppExtensions.BUNDLE
                    ){   // only check the merged options  NEW: avoid list lookup again (already done in _generateOptionIds)
                        productOptions._variantsInCurrency(optionsConfigData.options, null).then(() => productOptions.render());
                    } else {
                        // call render to show the new options
                        productOptions.render();
                    }
                }

                // NEW: wait for the init before merging (init is async when prices/stock are fetched)
                // INFO: no DOM event - .element changes when position before/after is used
                productOptions.ready.then(() => mergeOptions());

                // skip all other logic
                return true;
            }
        }

        // only if no explicit formSelector
        if(!formSelector){
            // default form selector product cart form  // NEW: online store 2.0  installments form (manual exclude to prevent options being added to the wrong form)
            formSelector = 'form[action*="/cart/add"]:not([id*=instal])'; // contains "instal" to exclude all installment forms
        }

        // locationMode 1 = custom selector
        let selector = this.config.locationMode === 1 ? this.config.selector : '';
        // position after container (autoPosition can overwrite)
        let position = this.config.locationMode === 1 ? this.config.position : undefined; // undefined fallback to default position (empty -> appendChild)

        // find where the field needs to be placed on the page
        let container = this._findOrCreateContainer(selector, product, formSelector);

        // NEW: hacky way to overwrite position in autoSelector mode
        if(this._autoPosition){
            position = this._autoPosition;
            delete this._autoPosition;
        }

        if(container){

            // IMPORTANT: deep copy of the config to support same config on multiple products ( config.options & config.previewConfig.layers ) contain references to values/controllers/objects
            let config = JSON.parse(JSON.stringify(optionsConfigData));

            //  locationMode 1 = custom selector
            let selectorPreview = this.config.previewLocationMode === 1 ? this.config.previewSelector : '';
            let positionPreview = this.config.previewLocationMode === 1 ? this.config.previewPosition : undefined; // undefined fallback to default position (empty -> appendChild)

            // always use container array loop to avoid duplicate code of new ProductOptions below
            let containers;

            // multiple containers found ? (case where the same product is shown twice e.g 2 featured products)
            if(Util.isNodeList(container)){
                containers = container;
            } else {
                containers = [container];
            }

            for (let i = 0; i < containers.length; i++) {
                const containerEl = containers[i];

                const previewContainerEl = config.preview ? this._findOrCreatePreviewContainer(selectorPreview, product, config) : undefined; // only find preview container if enabled

                // create product options  in the container and store in array
                const productOptions = new ProductOptions(config, {
                    api: this.config.api, assets: this.config.assets, queryParams: { shop: this.config.shop },
                    selector: containerEl, position: position, selectorPreview: previewContainerEl, positionPreview: positionPreview, selectorThumbnail: this.config.previewImgThumbnailSelector,
                    stickyPreview: this.config.previewSticky, stickyPreviewMobile: this.config.previewStickyMobile, stickyPreviewMobileSize: this.config.previewStickyMobileSize, previewZoom: this.config.previewZoom,
                    product: product, priceDraft: this.config.priceDraft, priceWeight: this.config.priceWeight,
                    lists: this.config.lists, // NEW: shared option lists
                    themes: this.config.themes // NEW: shared themes
                });

                this.products.push(productOptions);

                // NEW: Horizon 1.0+ requires re-init of preview on variant change
                // IMPROVEMENT: solution with a global flag to auto detect preview is removed with observer
                if(previewContainerEl && this.shopifyStore.isTheme('Horizon')) {
                    // we fully disable the re-init of the gallery if the gallery is used
                    if(config.previewConfig.gallery && previewContainerEl.tagName === 'MEDIA-GALLERY') {
                        previewContainerEl.replaceWith = () => { log.debug('app gallery no re-init required'); };
                    } else {
                        // Keep the existing section scope while supporting Horizon's
                        // legacy variant:update and standard shopify:product:select events.
                        this.shopifyStore.onProductVariantUpdate(previewContainerEl.closest('.shopify-section'), (e, update) => {
                            // wait for replacement
                            setTimeout(() => {
                                // find the new container after gallery replacement
                                const newPreviewContainerEl = this._findOrCreatePreviewContainer(
                                    config.previewSelector,
                                    productOptions.settings.product,
                                    config
                                );

                                if(newPreviewContainerEl && productOptions._preview) {
                                    // update the preview element reference
                                    productOptions._preview.element = newPreviewContainerEl;

                                    // move the wrapper to the new container
                                    if(productOptions._preview._wrapper && productOptions._preview._wrapper.parentNode) {
                                        newPreviewContainerEl.appendChild(productOptions._preview._wrapper);
                                    }

                                    // re-render the preview to ensure proper display
                                    productOptions._preview.renderPreview();

                                    log.debug('re-init preview on variant change:', update.variant, newPreviewContainerEl);
                                }
                            }, 100); // small delay to ensure DOM updates are complete
                        });
                    }
                }
            }

            return true;
        }

        return false;
    }

    _findOrCreateContainer(selector, product, formSelector) {

        let container;

        let form;

        if(formSelector){
            // search for form
            form = document.querySelector(formSelector);
        }

        const findContainer = (selector, form) => {
            // NEW: first scope to form if found ( required to support multiple featured products )
            if(form){
                container = form.querySelectorAll(selector);

                if(container.length > 0){
                    log.debug('using container in form with provided selector:', formSelector, selector, container);
                    return true;
                }
            }

            // selectorAll for multiple containers support
            container = document.querySelectorAll(selector);

            if(container.length > 0){
                log.debug('using container with provided selector:', selector, container);
                return true;
            }

            return false;
        }

        // explicit selector needs to be found
        if(selector){

            if(findContainer(selector, form)){
                return container;
            }

            log.debug('no container found with selector:', selector);
            return null;
        }

        // NEW: default container selector with explicit product.id  (not scoped to form)
        container = document.querySelector('[data-live-options="'+ product.id+'"]');

        if(container != null){
            log.debug('using default container with selector: [data-live-options]');
            return container;
        }

        // NEW: auto selector
        if(this.config.autoSelector){
            // find container with multiple and form scoped support
            if(findContainer(this.config.autoSelector, form)){

                // custom position
                if(this.config.autoSelectorPosition){
                    this._autoPosition = this.config.autoSelectorPosition;
                }

                return container;
            }
        }

        if (form && ThemeConfig.formInject) {

            // inject above add to cart
            let submit = form.querySelector("[type=submit],.add_to_cart,.btn.add-to-cart");
            container = document.createElement("div");

            container.className = 'inject'; // inject class as marker
            container.style.width = '100%'; // use full width of parent ( some themes require this - can be overwritten with css stylesheet and !important; )

            if (submit) {
                submit.parentNode.insertBefore(container, submit);
            } else {
                form.insertBefore(container, form.firstChild);
            }

            log.debug('injected container into form:', form);

            return container;
        }

        return null;
    }

    _findOrCreatePreviewContainer(selector, product, config){

        // NEW: online store 2.0 dawn theme slider auto find is not working - we hardcode custom selectors
        if(!selector && this.shopifyStore.isTheme('Dawn')){ // only if no manual selector provided
            // FALLBACK UNTIL MIGRATED: to auto selectors
            if(!this.config.autoPreviewSelector && !this.config.autoGallerySelector){
                if(config.previewConfig.gallery){
                    // auto overlay full slider with gallery  NEW: .product class prefix to get the right slider component
                    // NEW: newer dawn theme have media gallery wrapper element
                    if(document.querySelector('.product media-gallery')){
                        this.config.autoGallerySelector = '.product media-gallery';
                    } else {
                        this.config.autoGallerySelector = '.product slider-component'; // old dawn themes
                    }
                } else {
                    // replace first slider element with preview  (NEW: thumb exclude - some Dawn based themes have extra thumbnails)
                    this.config.autoPreviewSelector = '.product slider-component:not([class*="thumb"]) ul li:first-child';
                }
            }
        }
        else if(!selector && this.shopifyStore.isTheme('Horizon')) { // only if no manual selector provided

            // INFO: we always set the auto selectors (no server side theme config setup at the moment) AND dawn theme auto selectors are not reset in logic "theme.id config does not match"
            if(config.previewConfig.gallery){
                // auto overlay full slider with gallery  NEW: .product class prefix to get the right slider component
                this.config.autoGallerySelector = '.product-information media-gallery';
            } else {
                // INFO: we search for both variants Grid/Carousel(mobile always used)
                // INFO: we fix the first child (the theme puts the selected variant first) - works for most cases EXCEPT if custom layer.params.imgPos is used - in that case app gallery has to be used
                // IMPROVEMENT: custom imgPos and variant ID lookup could be supported in the future when the container lookup logic is refactored
                this.config.autoPreviewSelector = '.product-information media-gallery ul li:first-child, .product-information media-gallery slideshow-slide:first-child';
            }
        }

        let container;

        // NEW: personalize popup custom preview container
        container = this._checkPersonalizeContainer(config);

        if(container){
            return container;
        }

        const findContainer = (selector) => {

            // selectorAll to find all and find the best match based on the product
            let containers = document.querySelectorAll(selector);

            if(containers.length > 0){

                // find the most matching container
                if(containers.length > 1){
                    for (let i = 0; i < containers.length; i++) {

                        // NEW: different preview sections (desktop/mobile) support - required to exclude elements with no size (hidden)
                        if(ThemeConfig.previewHiddenExclude && Util.isMinSize(containers[i])){
                            log.warn('container preview found but hidden:', selector, containers[i]);
                            continue;
                        }

                        // only assign after hidden check
                        container = containers[i];

                        // product id in live preview (EDGE case case when custom selector provided)
                        if(container.className.indexOf(product.id) !== -1 || container.dataset.livePreview == product.id){
                            break;
                        }
                        // NEW: if multiple products are on the same page - we check the media id to find the right preview location (e.g Dawn featured products)
                        if(product.media?.length && container.innerHTML.indexOf(product.media[0].id) !== -1){
                            break;
                        }
                    }
                } else {
                    container = containers[0];  // single match

                    // NEW: different preview sections (desktop/mobile) support - required to exclude elements with no size (hidden)
                    if(ThemeConfig.previewHiddenExclude && Util.isMinSize(container)){
                        log.warn('container preview found but hidden:', selector, container);
                        container = null;
                    }
                }

                log.debug('using container preview with provided selector:', selector, container);
            }
        }

        let selectorExplicit = true;

        // NEW: default preview container selector without the need to configure on ui
        if(!selector){
            selector = '[data-live-preview="'+ product.id+'"]';
            selectorExplicit = false;
        }

        findContainer(selector);

        // explicit selector needs to be found
        if(selectorExplicit && !container){
            log.error('no container preview found with selector:', selector);
            return null;
        }

        // NEW: auto selectors
        if(!container){
            // gallery
            if(config.previewConfig.gallery && this.config.autoGallerySelector){
                findContainer(this.config.autoGallerySelector);
            } else if(this.config.autoPreviewSelector){
                findContainer(this.config.autoPreviewSelector);

                // NEW: dawn theme support for featured product (auto selector adjustments no good solution found - caused issues on the main product page)
                if(!container && this.shopifyStore.isTheme('Dawn')){
                    findContainer('.featured-product modal-opener');
                }
            }
        }

        // AUTO find container around main product image
        if(!container){

            let productImage;

            // check if media is available
            if(product.media && product.media.length){
                // default search for main image
                let imgIndex = 0;

                // NEW: support for custom image position for the image to replace based on the product layer config
                if(config.previewConfig && config.previewConfig.layers.length){
                    for (let i = 0; i < config.previewConfig.layers.length; i++) {
                        const layer = config.previewConfig.layers[i];
                        if(layer.type === 'product'){
                            if(layer.params.imgPos){
                                if(product.media.length > (layer.params.imgPos - 1)){
                                    imgIndex = layer.params.imgPos - 1;
                                }
                            }
                            break;
                        }
                    }
                }

                productImage = product.media[imgIndex];
            }
            // NEW: no media - try use featured image as fallback
            else if(product.featured_image) {
                productImage = { src: product.featured_image };
            }

            if(productImage){

                // number of parent nodes from image to use as container
                let imgParent = this.config.previewImgParent;
                // custom parent selector form found image
                let imgParentSelector = this.config.previewImgParentSelector;

                // lookup product images to find the main product image  INFO: we have to use basename because the image name has dimensions appended
                let productImageBaseName = Util.pathBaseName(productImage.src, false);

                let productImgElList = [];
                // NEW: main image selector (only img tag not specific preview container)
                if(this.config.previewImgMainSelector){
                    productImgElList = document.querySelectorAll(this.config.previewImgMainSelector);
                }
                // default no main image selector OR no match for previewImgMainSelector
                if(productImgElList.length === 0){
                    // some themes have the product image not rendered yet - search for (data-src/set) attributes
                    productImgElList = document.querySelectorAll('img[src*="'+productImageBaseName+'"], img[data-src*="'+productImageBaseName+'"], img[data-srcset*="'+productImageBaseName+'"]');
                }

                // filter all hidden images
                if(productImgElList.length && ThemeConfig.previewHiddenExclude){
                    productImgElList = Array.prototype.filter.call(productImgElList, function(el) {
                        return !Util.isHidden(el); // INFO: because we select <img> element - isHidden is enough instead of isMinSize
                    });
                }

                if(productImgElList.length > 0){

                    log.debug('found main product images:', productImgElList);

                    if(productImgElList.length === 1){
                        container = productImgElList[0];
                    } else {
                        // NEW: img.src search return multiple matches - we first look for the most matching src by subtracting the basename before comparing the pixels
                        let nameDiff = Number.MAX_VALUE;
                        // init with -1 to at least select one image if the size is still 0 (ios safari returns 0 when images are still loading)
                        let maxPixel = -1; // we select the biggest img found (to avoid selecting a thumbnail like a cart item)

                        for (let i = 0; i < productImgElList.length; i++) {
                            const productImgEl = productImgElList[i];

                            // actual dimensions
                            let pixel = productImgEl.offsetWidth * productImgEl.offsetHeight;

                            // pixel ZERO (image can be hidden) - parse shopify cdn dimension syntax to get the target pixel (important to avoid selecting the thumbnail which might be visible)
                            if(pixel === 0 && productImgEl.src){
                                let dimension = this.shopifyStore.parseImageDimension(productImgEl.src);
                                pixel = dimension.x * dimension.y;
                            }

                            // NEW: skip data urls for image src (some themes use loading placeholders - in this case we fallback to dataset[src/srcset] AND still last fallback to productImgEl.src )
                            let productImgElSrc = (productImgEl.src && productImgEl.src.indexOf('data:') !== 0 ? productImgEl.src : null);

                            // extract basename without extension
                            let baseName = Util.pathBaseName(productImgElSrc || productImgEl.dataset['src'] || productImgEl.dataset['srcset'] || productImgEl.src, false);
                            // important to remove dimensions to check the actual filename parts
                            baseName = this.shopifyStore.stripImageDimension(baseName);
                            let left = baseName.replace(productImageBaseName, '');
                            let diff = left.length;
                            // select if less diff then the previous
                            if(diff < nameDiff){
                                nameDiff = diff;
                                maxPixel = pixel; // IMPORTANT: also update max pixel
                                container = productImgEl;
                            }
                            // same image compare dimensions to find main image
                            else if(diff === nameDiff){

                                if(pixel > maxPixel){
                                    maxPixel = pixel;
                                    container = productImgEl;
                                }
                            }
                        }
                    }

                    log.debug('selected auto preview img:', container);

                    let parentContainer;

                    // parent selector from found image
                    if(imgParentSelector){
                        parentContainer = Util.findParent(container, imgParentSelector);
                    }

                    if(parentContainer != null){
                        container = parentContainer;
                        log.debug('using auto preview container with parent selector:', imgParentSelector, container);
                    } else if(container) {

                        // auto detected zoom/modals
                        if(imgParent === 1 && (container.parentElement && container.parentElement.getAttribute('data-zoom') !== null || container.parentElement.id.indexOf('Zoom') !== -1 || container.parentElement.className.indexOf('modal') !== -1)){
                            // 2 parents to prevent zoom/modals
                            imgParent = 2;
                        }

                        // traverse parent nodes based on imgParent number ( default 1 parent )
                        for(let p = 0; p < imgParent; p++){
                            container = container.parentElement;
                        }

                        log.debug('using auto preview container with parent:', imgParent, container);
                    }

                } else {
                    log.debug('failed to find main product image:', productImageBaseName);
                }

            }

        }

        if(container){

            // set min height to avoid layout shift when removing all child nodes
            if(!container.style.minHeight){
                container.style.minHeight = '1px';
            }

            // remove all childs of container ( we add our own preview element in the container )
            while (container.firstChild) {
                container.removeChild(container.lastChild);
            }

            // NEW: if we cannot replace the theme gallery (e.g the theme script is accessing the gallery elements and crashing)
            // we can apply data-live-preview-hide=""  to hide the theme gallery
            let themePreviewElList = document.querySelectorAll('[data-live-preview-hide="'+ product.id+'"]');
            if(themePreviewElList.length){
                for (let i = 0; i < themePreviewElList.length; i++) {
                    const themePreviewEl = themePreviewElList[i];
                    themePreviewEl.style.display = 'none';
                }
            }
        } else {
            log.warn('[live-options] no preview container found');
        }

        return container;
    }

    _checkPersonalizeContainer(config) {

        for (let i = 0; i < config.options.length; i++) {
            const option = config.options[i];

            if (option.type === 'personalize') {
                // only popup custom preview container element
                if(option.params.popup){
                    let container = document.createElement('div');
                    return container;
                }

                break;
            }
        }

        return null;
    }

    _getDiscount(){

        let discount = { type: DiscountType.NONE }; // no discount

        // check for automatic discount
        for (let i = 0; i < this._cart.items.length; i++) {
            const cartItem = this._cart.items[i];

            // automatic discount applied?
            if(cartItem.discounts && cartItem.discounts.length){
                discount.type = DiscountType.AUTO;
                discount.code = cartItem.discounts[0].title;
            }
        }

        if(discount.type === 0){
            // parse native discount cookie applied via url /discount/<code>
            let discountCode = this.shopifyStore.getDiscountCode();
            if(discountCode){
                discount.type = DiscountType.CODE;
                discount.code = discountCode;
            }
        }

        return discount;
    }

    _watchFormSubmit(){
        // USED FOR LOGGING ONLY
        this.shopifyStore.onCartFormSubmit((event) => {

            // exclude cart "update" submit (some themes use static update)
            // "In a form, the button and its value is only submitted if the button itself was used to submit the form."
            // we can detect if the update button was used, BUT only supported in chrome/firefox https://developer.mozilla.org/en-US/docs/Web/API/SubmitEvent/submitter
            if (event.submitter) {
                if (event.submitter.name && event.submitter.name === 'update') {
                    return;
                }
            }

            // serialize current cart
            let cartJson = JSON.stringify(this._cart);

            // cart api to storage the cart on storage
            let url = this.config.api + '/api/checkout/cart?shop=' + this.config.shop;

            // beacon to allow the request to be sent in the background
            navigator.sendBeacon(url, cartJson)

        }, ThemeConfig.cartFormCapture);
    }

    _watchCartItems() {

        // fix DEBUT theme properties cart update bug (affects <= 17.5.0)
        if(window.theme && window.theme.Cart && window.theme.Cart.prototype && window.theme.Cart.prototype._setProductDetailsList){
            const _original = window.theme.Cart.prototype._setProductDetailsList;
            window.theme.Cart.prototype._setProductDetailsList = function(item, productDetailsList){  // patch method
                // INFO: we filter all undefined property items (all properties with "_" or empty value are undefined)
                _original(item, productDetailsList.filter(n => n)); // call original
            }
        }

        // process on page ready
        this._processCartItems();

        // makes sure to execute only once after a delay when called multiple times ( delay is important for the theme to re-render the cart )
        let processDelayed = Util.debounced(1000, () => this._processCartItems());

        this._processPending = 0;

        // listen to ajax calls that might update the theme
        const listener = AjaxWatch.register((ajax) => {
            // this regex checks cart*  ( to catch all cart related requests )
            if(/.*cart.*/.test(ajax.url)){
                // reset cache for POST requests (changes to the cart)
                // IMPORTANT: use cache for all GET request to avoid endless loop by _processCartItems > shopifyCart.getCart > AjaxWatch  || NEW: some themes use GET request for modification .. manual check required...
                if(ajax.method && ajax.method.toString().toUpperCase() === 'POST' || (ajax.url.indexOf('/cart/add') !== -1 || ajax.url.indexOf('/cart/change') !== -1)){
                    delete this.shopifyCart.cache; // INFO: cache reset causes double _processCartItems because this.shopifyCart.getCart triggers AjaxWatch (no issue - event better to support theme html updates)
                }

                this._processPending++;
                // NEW: when other apps also watch ajax calls - this can lead to endless loops - so we limit the process queue to max 2 times
                if(this._processPending > 2){
                   this._processPending = 0;
                   log.warn('[live-options] cart process skipped to avoid endless loop');
                   return; // prevent endless loop
                }

                processDelayed();
            }
        });

        // for themes that re-render the cart on page load ( the ajax listener might not be registered yet )
        setTimeout(() => processDelayed(), 2000); // 2 seconds delay + 1 second processDelayed
    }

    _watchAjaxAdd(){
        this._ajaxInjectUnregister = AjaxWatch.registerBefore((ajax) => {

            // only execute for cart add requests
            if (!AjaxWatch._isCartAddRequest(ajax)) {
                return;
            }

            return this._injectAjaxProperties(ajax);
        });
    }

    _watchExternalSubmit(){

        this._onProductSubmit = (event) => {
            if(!this.products || this.products.length === 0){
                return;
            }

            // forward submit event to all product options instances
            for (let i = 0; i < this.products.length; i++) {
                const productOptions = this.products[i];
                if(productOptions && typeof productOptions._handleFormSubmit === 'function'){
                    productOptions._handleFormSubmit(event);
                }
            }
        };

        window.addEventListener('cloudlift.submit', this._onProductSubmit);
    }

    _injectAjaxProperties(ajax){
        if(!this.products || this.products.length === 0){
            return;
        }

        for (const productOptions of this.products) {
            if(!productOptions || !productOptions._productForm || !productOptions._variantId){
                continue;
            }

            // sync-only submit: run form property updates without async export/cart side effects
            const properties = productOptions._getCartProperties();
            if(!properties || Object.keys(properties).length === 0){
                continue;
            }

            const updatedBody = AjaxWatch.injectCartAddProperties(ajax, {
                variantId: productOptions._variantId,
                properties
            });

            if(updatedBody !== undefined){
                return updatedBody;
            }
        }
    }

    _processCartItems(){
        // get the cart via api
        // INFO: we always try to use the cache (we manually reset the cache in AjaxWatch above)
        return this.shopifyCart.getCart(true).then( cart => {

            // NEW: track process pending to avoid endless loop - we always reset to 0 - after one process all should be updated as we use a cached cart
            this._processPending = 0;

            this._cart = cart; // most updated cart reference (used for draft orders/discounts)

            for (let i = 0; i < cart.items.length; i++) {
                let item = cart.items[i];

                if(!this._processCartItem(cart, item, i)){
                    return; // stop processing (error or page refresh)
                }
            }

            this._checkDiscount(cart);

            // NEW: save for later of the cart - rendered into the cart app block ( no automatic placement )
            CartSave.mount({ api: this.config.api, assets: this.config.assets, shop: this.config.shop }, this.shopifyCart);

            // NEW: moneyFormat wrapped with an element with a class attribute => indicates custom currency converter => trigger window resize to execute converter logic
            if(ThemeCurrency.moneyFormat.indexOf('class') !== -1){
                window.dispatchEvent(new Event('resize'));
            }

            // NEW: allow checkout button/element to be disabled until the app is loaded
            let enabledElList = document.querySelectorAll('[data-live-enabled]');

            if(enabledElList.length){
                for (let i = 0; i < enabledElList.length; i++) {
                    const enabledEl = enabledElList[i];
                    // reset disabled flag
                    if(enabledEl.disabled === true){
                        enabledEl.disabled = false;
                    }
                    // if hidden show
                    if(enabledEl.style.display === 'none'){
                        enabledEl.style.display = 'block';
                    }
                }
            }
        });
    }

    _processCartItem(cart, item, index){

        // default values
        let quantity = item.quantity;

        // process properties
        if(item.properties){

            let processNode = false;

            // check if item is added via this app
            if(item.properties.hasOwnProperty(CartItemProperties.OPTIONS) || item.properties.hasOwnProperty(CartItemProperties.ID)){
                processNode = true;
            }

            if(processNode){

                // check if currency changed - update stored price options to show on UI in the current currency
                if(ThemeCurrency.currencyConvert){

                    let priceOptionsItem = CartOptionsPrice.getItem(item.properties[CartItemProperties.ID]);

                    if(priceOptionsItem?.currency && priceOptionsItem.currency !== ThemeCurrency.currency){
                        // convert individual options
                        this._convertOptionsPriceProperties(priceOptionsItem.properties);
                        // update currency
                        priceOptionsItem.currency = ThemeCurrency.currency;
                        // update storage
                        CartOptionsPrice.storeItem(item.properties[CartItemProperties.ID], priceOptionsItem);
                    }

                    // INFO: we used to update the cartItem.properties:
                    // https://github.com/michaelhaessig/cloudlift-app-options/commit/ed4321ad23c8bf6489617049c1d8e19e79c735c7#diff-0a61c902af14718e8730855dfa53f630507bc48aebcdb8773d85554eb1dfd430L1175
                }

                // NEW: cart item priceOptions processing without the need to find a cart element (required for checkout without cart shown on page)
                let priceOptions = 0;

                // main product with options
                if (item.properties[CartItemProperties.ID] && item.properties.hasOwnProperty(CartItemProperties.OPTIONS) && item.properties[CartItemProperties.PRICE]) {
                    // options price of a single quantity
                    priceOptions = parseInt(item.properties[CartItemProperties.PRICE]);
                }

                // NEW: custom new final price with a discount applied (quantity discounts)
                let priceDiscount = null;

                // find the nodes that wraps the item
                let itemElList = this._resolveCartItemEl(cart, item);

                if(itemElList.length === 0){
                    log.warn('[live-options] missing cart item element for: ', item);
                }

                // NEW: some themes can have the cart multiple times!
                for (let i = 0; i < itemElList.length; i++) {
                    const itemEl = itemElList[i];

                    // set quantity 0 to calculate a total with only the main products
                    if(!item.properties.hasOwnProperty(CartItemProperties.OPTIONS)){
                        quantity = 0;
                    }

                    // process item element
                    if(itemEl){

                        // default Main view property
                        let previewProperty = CartItemProperties.PREVIEW;

                        let previewSrc = item.properties[previewProperty];

                        // NEW: support other views than Main showing as cart image
                        if(!previewSrc && ThemeConfig.cartItemPreviewViews){
                            for (const property in item.properties) {
                                // find first property that contains a preview image
                                if (item.properties.hasOwnProperty(property) && property.startsWith(previewProperty) && item.properties[property]) {
                                    previewSrc = item.properties[property];
                                    break;
                                }
                            }
                        }

                        // update preview
                        if(previewSrc){

                            // preview selectors
                            let imgEl = itemEl.querySelector('[data-live-item-image]');

                            // not found => auto search product image on item
                            if(!imgEl && ThemeConfig.cartItemPreview) {

                                let itemBaseName = Util.pathBaseName(item.image, false);

                                imgEl = itemEl.querySelector('img[src*="'+itemBaseName+'"], img[data-src*="'+itemBaseName+'"], img[data-srcset*="'+itemBaseName+'"]');
                            }

                            if(imgEl){
                                imgEl.src = previewSrc;

                                // IMPORTANT: remove all custom src attributes to make sure image is replaced (some themes use it for lazy loading)
                                if(imgEl.hasAttribute('srcset')){
                                    imgEl.removeAttribute('srcset');
                                }
                                if(imgEl.hasAttribute('data-srcset')){
                                    imgEl.removeAttribute('data-srcset');
                                }
                                if(imgEl.hasAttribute('data-src')){
                                    imgEl.removeAttribute('data-src');
                                }
                            }
                        }

                        // transform all upload links if no Upload-Lift loaded
                        // IMPORTANT: to support Upload-Lift thumbnail preview
                        // INFO: this can still return null if the options script is load sync before the upload.js script
                        if(!window.Cloudlift.upload){
                            for(const property in item.properties){
                                if(item.properties.hasOwnProperty(property)){
                                    let link = item.properties[property];
                                    // check if value is an upload link
                                    if(typeof link == 'string' && link.indexOf('cloudlift.app') !== -1){

                                        // transform upload links not yet transformed inside this itemEl
                                        // INFO: we cannot search innerHTML as the links could already be transformed as links on the server side  NEW: escapeRegex required to match all links (export urls)
                                        let uploadLinkElements = Util.findElementContainsText('*', Util.escapeRegex(link), itemEl);

                                        if(uploadLinkElements.length > 0){
                                            // the last element is the most matching containing only the text
                                            let linkEl = uploadLinkElements[uploadLinkElements.length - 1];

                                            // COPY from Upload-Lift
                                            let replacementNode = document.createElement('span');
                                            replacementNode.className = 'cl-prop';
                                            replacementNode.innerHTML = linkEl.textContent.replace(link, '<a href="'+link+'" target="_blank" style="cursor:pointer;border:0;">'+Translate.get('product.options.file.link')+'</a>');
                                            linkEl.parentNode.replaceChild(replacementNode, linkEl);
                                        }

                                    }
                                }
                            }
                        }

                        // explicit button support
                        let editBtnEl = itemEl.querySelector('[data-live-item-edit]');

                        // not found => auto add edit button
                        if(!editBtnEl && ThemeConfig.cartItemEdit) {

                            let editBtnLocationEl = this._resolveItemOptionsLocation(item, itemEl);
                            let editBtnPosition = 'afterend'; // default show after options

                            // OLD: find remove button (kept to avoid breaking currently working stores)
                            if(!editBtnLocationEl && Object.keys(item.properties).filter(key => !key.startsWith('_') && item.properties[key]).length){ // only if properties are defined
                                editBtnLocationEl = itemEl.querySelector('a[href*="/cart/change"]');
                                // insert before
                                editBtnPosition = 'beforebegin';
                            }

                            if(editBtnLocationEl != null){

                                log.debug('---> adding editBtn: ' + editBtnPosition, editBtnLocationEl);

                                editBtnEl = document.createElement('a');
                                editBtnEl.style.padding = '5px 10px'; // INFO: direct style because no uuid scoping
                                editBtnEl.style.border = '1px solid #d1d5db';
                                editBtnEl.style.borderRadius = '3px';
                                editBtnEl.innerText = Translate.get('product.options.item.edit');
                                editBtnEl.dataset.liveItemEdit = '1'; // flag to avoid adding again (gets found on next processing)
                                // add wrapper div
                                let wrapper = document.createElement('div');
                                wrapper.style.padding = '10px 0';
                                wrapper.appendChild(editBtnEl);

                                // insert on provided position
                                editBtnLocationEl.insertAdjacentElement(editBtnPosition, wrapper);
                            }
                        }

                        if(editBtnEl){

                            log.debug('---> using editBtn element', editBtnEl);

                            let editUrl = item.url + '&item=' + item.key;

                            if(editBtnEl.tagName === 'A'){
                                // default link
                                editBtnEl.href = editUrl;
                            } else {
                                // click handler
                                editBtnEl.addEventListener('click', () => window.location.href = editUrl);
                            }
                        }

                        // auto hide hidden options properties
                        if(ThemeConfig.cartItemClOptions){
                            for (let property in item.properties) {
                                if(item.properties.hasOwnProperty(property)){
                                    if (property.startsWith(CartItemProperties.OPTIONS)) {

                                        // quick check if property is visible
                                        if(itemEl.innerHTML.indexOf(property) !== -1){

                                            // regex search to find key:value
                                            let clPropertyValueElements = Util.findElementContainsRegex('*', new RegExp(`${property}.*${item.properties[property]}`, 's'), itemEl);

                                            if (clPropertyValueElements.length > 0) {

                                                // the last element is the most matching containing only the text
                                                let valueEl = clPropertyValueElements[clPropertyValueElements.length - 1];

                                                // IMPORTANT: check if the found element only contains this property (some themes have a single element with all properties)
                                                let remove = true;
                                                for (let nextProperty in item.properties) {
                                                    if (item.properties.hasOwnProperty(nextProperty)) {
                                                        if(valueEl.innerHTML.indexOf(nextProperty) !== -1){
                                                            remove = false;
                                                            break;
                                                        }
                                                    }
                                                }

                                                // remove if element only contains this property
                                                if(remove){
                                                    valueEl.parentNode.removeChild(valueEl);
                                                } else {
                                                    // manual replace to avoid removing all properties  IMPORTANT: innerHTML to avoid removing tags
                                                    let propertyValue = String(item.properties[property]); // make sure property value is a string
                                                    let start = valueEl.innerHTML.indexOf(property);
                                                    let end = valueEl.innerHTML.indexOf(propertyValue) + propertyValue.length;
                                                    valueEl.innerHTML = valueEl.innerHTML.replace(valueEl.innerHTML.slice(start, end),'');
                                                }

                                            }
                                        }

                                    }
                                }

                            }
                        }

                        // bundle products OR (opt-in) options price only expanded with the add-on product (checkout update mode has no components)
                        let bundleLayout = item.properties[CartItemProperties.BUNDLE]
                            || (ThemeConfig.bundleCartLayoutOptions && AppExtensions.BUNDLE && item.has_components && item.properties[CartItemProperties.PRICE] && ThemeCurrency.checkout.variantId);

                        // ID is only set if a price add-on is used
                        if(item.properties[CartItemProperties.ID]){

                            // main product with options
                            if(item.properties.hasOwnProperty(CartItemProperties.OPTIONS)){

                                // show individual options prices    HACKY: avoid showing prices if bundle extension was used (there could be a discount on the add-on price which is not reflected in this UI)
                                if(ThemeConfig.cartItemPriceOptions && !bundleLayout) {

                                    // find the price options product and display the prices on the main product
                                    let priceOptionsItem = CartOptionsPrice.getItem(item.properties[CartItemProperties.ID]);

                                    if(priceOptionsItem?.properties){

                                        for (let property in priceOptionsItem.properties) {
                                            // skip hidden property
                                            if (property.charAt(0) === '_') {
                                                continue;
                                            }

                                            // find matching property
                                            if (priceOptionsItem.properties.hasOwnProperty(property) && item.properties.hasOwnProperty(property)) {

                                                // INFO: we use a regex to find each property together with its value - required to support multiple properties with the same value e.g (engraving: yes / gift: yes)
                                                //let propertyValueElements = Util.findElementContainsRegex('*', new RegExp(`${property}.*${item.properties[property]}|${item.properties[property]}.*${property}`), itemEl);
                                                let propertyValueElements = Util.findElementContainsRegex('*', new RegExp(`${Util.escapeRegex(property)}.*${Util.escapeRegex(item.properties[property])}`, 's'), itemEl);

                                                if (propertyValueElements.length > 0) {

                                                    // the last element is the most matching containing only the text
                                                    let valueEl = propertyValueElements[propertyValueElements.length - 1];

                                                    // clean property to get a valid attribute
                                                    let propertyValid = property.replace(/^[^a-zA-Z]+|[^\w]+/g, "");

                                                    // only process once (suffix property to support same element with multiple properties)
                                                    const flag = 'p' + propertyValid.charAt(0).toUpperCase() + propertyValid.slice(1); // hacky way to get camel case data attribute
                                                    if (!valueEl.dataset[flag]) {

                                                        log.debug('--> found element to display options price for property: ' + property, valueEl);

                                                        let propertyValue = item.properties[property];
                                                        // NEW: support "&" in property value (shopify theme can auto escape it) in this case we also escape it to make sure we can replace the value
                                                        if(propertyValue.indexOf('&') !== -1 && valueEl.innerHTML.indexOf('&amp;') !== -1){
                                                            propertyValue = propertyValue.replace(/&/g, '&amp;');
                                                        }

                                                        // add the price value to the end of the value
                                                        valueEl.innerHTML = valueEl.innerHTML.replace(propertyValue, propertyValue + " | " + priceOptionsItem.properties[property]);

                                                        // mark as processed
                                                        valueEl.dataset[flag] = '1';
                                                    }

                                                }


                                            }
                                        }

                                    }
                                }

                            }

                        }

                        // Bundle extension item layout
                        // cart API .has_components set for bundled products
                        // individual components currently only available in liquid: https://shopify.dev/docs/api/liquid/objects/line_item#line_item-item_components
                        if(item.has_components && bundleLayout){

                            // explicit location support
                            let bundleItemEl = itemEl.querySelector('[data-live-item-bundle]');

                            // not found => auto add layout wrapper
                            if(!bundleItemEl && ThemeConfig.bundleCartLayout){
                                // show below edit button OR resolve options
                                let bundleLocationEl = editBtnEl || this._resolveItemOptionsLocation(item, itemEl);

                                if(bundleLocationEl != null){

                                    log.debug('---> adding bundle layout: ', bundleLocationEl);

                                    bundleItemEl = document.createElement('div');
                                    bundleItemEl.dataset.liveItemBundle = '1'; // flag to avoid adding again (gets found on next processing)

                                    // add below
                                    bundleLocationEl.insertAdjacentElement('afterend', bundleItemEl);
                                }
                            }

                            if(bundleItemEl){
                                this._renderBundleLayout(item, bundleItemEl);
                            }
                        }

                    } else {
                        // only log if main product item
                        if(item.properties.hasOwnProperty(CartItemProperties.OPTIONS)){
                            log.debug('--> no cart item node found for item: ', item);
                        }
                    }

                }


            }
        }

        return true;
    }

    _resolveCartItemEl(cart, item) {

        let itemElList = [];

        // items selectors on common themes     NEW: item.id selectors (not perfect but works because we sequentially remove price addons)
        let cartItemSelectorList = ['[data-live-item-key="'+item.key+'"]', '[data-line-item-key="'+item.key+'"]', '[data-cart-item-key="'+item.key+'"]', '.cart_item_' + item.id];

        // lookup via selector
        for (let i = 0; i < cartItemSelectorList.length; i++) {
            const selector = cartItemSelectorList[i];
            // single match  , NEW: ALL to support multiple carts on the same page
            itemElList = document.querySelectorAll(selector);
            // return first match
            if(itemElList.length){
                return itemElList;
            }
        }

        // fallback via find item via image
        if(item.image && ThemeConfig.cartItem){

            log.debug('--> [cart-item] image fallback ', item);

            let itemEl;

            // find all images on page that contain the cart image
            let itemBaseName = Util.pathBaseName(item.image, false);

            // some themes have the product image not rendered yet - search for (data-src/set) attributes
            let imgElSelectors = ['img[src*="'+itemBaseName+'"]', 'img[data-src*="'+itemBaseName+'"]', 'img[data-srcset*="'+itemBaseName+'"]'];

            // NEW: itemImageParent
            if(this.config.itemImgParentSelector){
                let parentSelectors = this.config.itemImgParentSelector.split(',');
                let scopedSelectors = [];
                for (let i = 0; i < parentSelectors.length; i++) {
                    const parentSelector = parentSelectors[i];
                    for (let j = 0; j < imgElSelectors.length; j++) {
                        const imgElSelector = imgElSelectors[j];
                        scopedSelectors.push(parentSelector + ' ' + imgElSelector);
                    }
                }
                // update img selectors scoped to parent
                imgElSelectors = scopedSelectors;
            }

            // NEW: dynamic selectors
            let imgElList = document.querySelectorAll(imgElSelectors.join(','));

            // not found OR already updated
            if(imgElList.length > 0){

                // INFO: multiple image can be found if the same product is added to the cart multiple times
                for (let i = 0; i < imgElList.length; i++) {
                    const imgEl = imgElList[i];

                    // NEW: first use parent selector if provided
                    if(this.config.itemImgParentSelector){
                        let parentEl = Util.findParent(imgEl, this.config.itemImgParentSelector);
                        if(parentEl && !parentEl.dataset.liveItemKey){ // IMPORTANT: skip products with same image that are already resolved
                            // mark match with key to find faster next time (also required when we update the image to find the item again)
                            parentEl.dataset.liveItemKey = item.key;

                            log.debug('resolved cart item node via parent selector ', this.config.itemImgParentSelector, parentEl);

                            return [ parentEl ];
                        }
                    }

                    let cartForm = Util.findParent(imgEl, 'form');

                    // no cart form ( could be popup cart with only one item )
                    if(!cartForm){
                        if(imgElList.length === 1){
                            itemEl = imgElList[0].parentNode;
                            itemEl.dataset.liveItemKey = item.key; // IMPORTANT: we have to mark the item to find it again (no item found can skip price add-on calc)
                            return [ itemEl ];
                        }
                        // skip
                        continue;
                    }

                    // INFO: this does not find the absolute top level cart item node (just a wrapper around the image that also contains the unique cartItem.key)
                    let currentNode = imgEl;
                    // traverse parent nodes to find the best match (stop at form)
                    while(currentNode !== cartForm){

                        // check if we can find the item.key (which indicates this node contains this exact cart item)
                        if(currentNode.innerHTML.indexOf(item.key) !== -1){
                            let match = true;
                            // IMPORTANT: we could be one level below the cart form which then includes all cart items
                            if(cart.items.length > 1){
                                // check if this node contains other items
                                for (let j = 0; j < cart.items.length; j++) {
                                    const cartItem = cart.items[j];
                                    // exclude self
                                    if(cartItem !== item){
                                        // if this node also contains other cartItem.key then we are on the wrong level and wrong item image
                                        if(currentNode.innerHTML.indexOf(cartItem.key) !== -1){
                                            match = false;
                                            break;
                                        }
                                    }
                                }
                            }

                            // single match => return as itemEl
                            if(match){
                                // mark match with key to find faster next time (also required when we update the image to find the item again)
                                currentNode.dataset.liveItemKey = item.key;

                                log.debug('auto resolved cart item node ', currentNode);

                                return [ currentNode ];
                            } else {
                                break; // stop this while loop => check next item
                            }
                        }

                        // next parent
                        currentNode = currentNode.parentElement;
                    }

                }

                // no cart key use first img parent
                let imgEl = imgElList[0];
                // NEW: fallback to <tr> <li> <div> before using the direct parent
                itemEl = Util.findParent(imgEl, 'tr') || Util.findParent(imgEl, 'li') || Util.findParent(imgEl, 'div') || imgEl.parentNode;
                itemEl.dataset.liveItemKey = item.key; // IMPORTANT: we have to mark the item to find it again

                log.debug('fallback cart item parent ', itemEl);
            }

            // auto resolved parent of image
            return [ itemEl ];
        }

        return itemElList;
    }

    _checkDiscount(){

        this._discountEl = document.querySelector('[data-live-discount]');

        // auto add discount
        if(!this._discountEl && ThemeConfig.cartDiscount){

            // NEW: dawn theme pre defined auto discount location
            if(this.shopifyStore.isTheme('Dawn')){
                let cartBlocks = document.querySelector('.cart__blocks, .drawer__footer');
                if(cartBlocks){
                    this._discountEl = document.createElement('div');
                    this._discountEl.dataset.liveDiscount = ''; // set data attribute to find discount again (required for themes that remove full sections)
                    cartBlocks.insertBefore(this._discountEl, cartBlocks.firstChild);
                }
            }

            // default logic via subtotal
            if(!this._discountEl){
                let moneyFormatAmount = this.shopifyStore.trimMoneyFormatAmount(ThemeCurrency.moneyFormat);

                // INFO: find total price
                let priceEl = this._findPriceEl('form[action*="/cart"]:not(form[action*="/cart/add"]) *, form[action*="/checkout"] *', this._cart.total_price, moneyFormatAmount);

                if(priceEl){

                    let parentNode = priceEl.parentNode;
                    while(parentNode){
                        //  exclude p tags - mostly not a good location
                        if(parentNode.tagName !== 'P'){
                            let display = getComputedStyle(parentNode).display;

                            // find first parent with display block ( best fit to avoid messed up layouts )
                            if(display === 'block' || display === 'flex'){
                                this._discountEl = document.createElement('div');
                                this._discountEl.dataset.liveDiscount = '1'; // flag to find on next call

                                // append above
                                parentNode.insertBefore(this._discountEl, parentNode.firstChild);

                                break;
                            }
                        }

                        // next
                        parentNode = parentNode.parentNode;
                    }

                }
            }
        }

        if(this._discountEl != null){

            let discount = this._getDiscount();

            // auto discount always valid
            if(discount.type === DiscountType.AUTO) {
                discount.valid = true;
                // auto discount always overwrites previous discount (cart total already updated by shopify theme)
                delete this._discount;
            }

            // init with current discount  (supports AUTO discount toggle)
            if(!this._discount || this._discount.code !== discount.code){
                this._discount = discount;

                this._discountInput = { code: '', loading: false, button: Translate.get('product.options.discount.button'), placeholder: Translate.get('product.options.discount.placeholder') };

                if(this._discount.type !== DiscountType.NONE) {
                    // INFO: we never re-fetch discount info (only check localStorage) - draft checkout api will still include the discount if the cookie is present
                    if(window.localStorage){
                        let discountJson = localStorage.getItem('cl_discount');
                        if (discountJson) {
                            let discountStorage = JSON.parse(discountJson);
                            if (discountStorage.code === this._discount.code) {
                                this._discount = discountStorage;
                            }
                        }
                    }

                    // not yet validated > auto apply
                    if(!this._discount.valid) {
                        this._discountInput.code = this._discount.code;
                        this._applyDiscount();
                    }
                }

                // NEW: check if discount form is inside cart form (required to apply discount when no draft order is used)   ( Selector: cart with wildcard & excludes of product form & dawn theme 2nd form )
                let cartFormSelector = 'form[action*="/cart"]:not(form[action*="/cart/add"]):not([id="cart-notification-form"]), form[action*="/checkout"]';
                let cartForm = Util.findParent(this._discountEl, cartFormSelector);
                // not found > find the cart form on the page to create link on the input
                if(!cartForm){
                    cartForm = document.querySelector(cartFormSelector);
                    if(cartForm){
                        let formId = cartForm.getAttribute('id');
                        // IMPORTANT: only add if defined - empty or wrong reference will break discount being added to the cart
                        if(formId){
                            this._discountInput.cartFormLink = formId;
                        }
                    }
                }
            }

            this._renderDiscount();
        }
    }

    _removeDiscount(event){
        if(event) {
            // prevent form submit
            event.preventDefault();
            event.stopImmediatePropagation();
        }

        this._discount.type = DiscountType.NONE;
        this._discount.code = '';
        this._discount.valid = false;

        // remove cookie
        this.shopifyStore.removeDiscountCode();

        this._renderDiscount();
    }

    _applyDiscount(event){
        if(event){
            // prevent form submit
            event.preventDefault();
            event.stopImmediatePropagation();
        }

        // ignore empty code
        if(!this._discountInput.code){
            return;
        }

        // map input to discount
        this._discount.type = DiscountType.CODE;
        this._discount.code = this._discountInput.code;
        this._discount.valid = false;

        // ui flags
        this._discountInput.loading = true;
        this._discountInput.error = '';
        this._discountInput.success = '';

        // map items to calculate discount value  INFO: value never shown
        let items = [];

        for (let i = 0; i < this._cart.items.length; i++) {
            const cartItem = this._cart.items[i];

            // convert to item that only contains the required attributes
            let item = this.shopifyCart.toItem(cartItem.id, cartItem.quantity, cartItem.properties);

            // product id used for discount filter  TODO: this is not filtered on the server side yet (valid discount is only checked)
            item.productId = cartItem.product_id;

            items.push(item);
        }

        // object to send to api
        const checkout = {
            items: items,
            discount: this._discount, // type 0 = no discount selected
            currency: this._cart.currency, // NEW: draft orders custom currency support
            country: window.Shopify ? window.Shopify.country : undefined // NEW: current country for shopify markets pricing support
        }

        let request = new Request(this.config.api + '/api/checkout/discount', {
            method: 'POST',
            data: {checkout},
            responseType: 'json',
            headers: {
                'Content-type': 'application/json'
            },
            queryParams: {shop: this.config.shop}
        });

        // show loading spinner
        this._renderDiscount();

        return request
            .then((response) => {

                this._discountInput.loading = false;

                // only valid discount return data
                if(response.data){
                    // save updated discount
                    this._discount = response.data;
                    this._discount.valid = true;

                    // clear input code
                    this._discountInput.code = '';

                    if(window.localStorage){
                        localStorage.setItem('cl_discount', JSON.stringify(this._discount));
                    }

                    // save default cookie to persist across page reload
                    this.shopifyStore.saveDiscountCode(this._discount.code);

                    this._discountInput.success = Translate.get('product.options.discount.valid');
                } else {
                    // show error
                    this._discountInput.error = Translate.get('product.options.discount.invalid');
                }

                this._renderDiscount();

                log.debug('[live-options-api] checkout discount response', response.data);

            })
            .catch((error) => {

                // SERVER-ERROR: (should not happen often)
                this._discountInput.loading = false;
                this._discountInput.error = 'Please try again';

                this._renderDiscount();

                log.error('[live-options-api] checkout discount error', error);
            });
    }

    _renderDiscount(){
        render(discountCodeTemplate(this._discount, this._discountInput, this._applyDiscount.bind(this), this._removeDiscount.bind(this), (e) => {
            // map input value
            this._discountInput.code = e.target.value;
            // reset error if set
            if(this._discountInput.error){
                this._discountInput.error = '';
                this._renderDiscount();
            }
        }), this._discountEl);
    }

    _findPriceEl(selector, price, moneyFormatAmount, element) {

        // get price formatted in theme currency
        let priceMoney = formatMoney(price, moneyFormatAmount);

        let priceElements;

        // NEW: hacky way to only find element 0.00 and not e.g 10.00
        if(price === 0 || ThemeConfig.cartPriceRegex){
            // INFO: this could probably by solved differently be actually tagging already updated elements (e.g el.classList.add('cl-price')) - but this would require significant rewrite of the price finding logic
            priceElements = Util.findElementContainsRegex(selector, new RegExp(`[^0-9]${priceMoney}[^0-9]`, 's'), element);
        } else {
            // DEFAULT: finds the most matches - currently in use on lots of stores
            priceElements = Util.findElementContainsText(selector, priceMoney, element);
        }

        if(priceElements.length === 0){
            log.debug('no price elements found for price:', priceMoney, element);

            if(ThemeConfig.amountNoDecimals){
                priceMoney = formatMoney(price, '{{ amount_no_decimals }}');

                log.debug('fallback {{ amount_no_decimals }} price: ', priceMoney, element);

                priceElements = Util.findElementContainsText('*', priceMoney, element);
            }
        }

        // select most specific match element
        if(priceElements.length){
            return priceElements[priceElements.length - 1];
        }

        return null;
    }

    // NEW: for UI only convert each individual option price into the current currency
    _convertOptionsPriceProperties(properties) {
        for (let property in properties) {
            if (property.charAt(0) === '_') {
                continue;
            }
            // base price is stored as hidden property with the same name
            if(properties.hasOwnProperty(property) && properties.hasOwnProperty('_' + property)){
                let optionPriceBase = parseInt(properties['_' + property]);
                // convert to current currency
                optionPriceBase = currencyConvert(optionPriceBase);
                // format the new price
                properties[property] = formatMoney(optionPriceBase * 100, ThemeCurrency.moneyFormat).replace(/(<([^>]+)>)/gi, ''); // cut html in case moneyFormat contains it
            }
        }
    }

    _resolveItemOptionsLocation(item, itemEl){
        let locationEl;

        // NEW: find product options for better automatic location support
        let propertyParentElList = [];

        // copy to add variant option
        let properties = { ...item.properties };

        // exclude all private and empty properties
        for (const property in properties) {
            if (property.charAt(0) === '_' || properties[property] === '') {
                delete properties[property];
            }
        }

        // NEW: add variant options to also find without custom options
        if(item.options_with_values?.length){
            for (const option of item.options_with_values) {
                // only add if not already added (option with same name)
                if(!properties.hasOwnProperty(option.name)){
                    properties[option.name] = option.value;
                }
            }
        }

        for (const property in properties) {

            // find property (same logic as options price patching below)
            let propertyElements = Util.findElementContainsRegex('*', new RegExp(`${Util.escapeRegex(property)}.*${Util.escapeRegex(properties[property])}`, 's'), itemEl);

            if (propertyElements.length > 0) {

                // find common parent
                if(propertyParentElList.length){
                    for (let j = propertyElements.length -1; j >= 0; j--) {
                        const propertyEl = propertyElements[j];
                        // check if this element exist in the previous found list of property elements (this indicates that it's a wrapper around all properties)
                        if(propertyParentElList.indexOf(propertyEl) !== -1){
                            // set location to the common parent
                            locationEl = propertyEl;
                            break;
                        }
                    }
                    if(locationEl){
                        break;
                    }
                } else {
                    // first property > add all found elements
                    propertyParentElList.push(...propertyElements);
                }
            }
        }

        // no location yet but single property was found use the first found element
        if(!locationEl && propertyParentElList.length){
            locationEl = propertyParentElList[propertyParentElList.length - 1];
        }

        return locationEl;
    }

    async _renderBundleLayout(item, bundleItemEl){

        let bundleProducts = JSON.parse(item.properties[CartItemProperties.BUNDLE] || '[]');

        // add main product if not included (only included if discounted)
        let mainProduct = bundleProducts.find(p => p.id === '0');
        if(!mainProduct){
            mainProduct = {
                handle: item.handle,
                id: '0',
                quantity: 1
            };
            bundleProducts.unshift(mainProduct);
        }

        let priceOptions = 0;
        let priceOptionsBase = 0;
        let priceProduct = false;

        if(item.properties[CartItemProperties.PRICE]){
            // base to convert correctly
            if(item.properties[CartItemProperties.PRICE_BASE]){
                priceOptionsBase = parseFloat(item.properties[CartItemProperties.PRICE_BASE]) / 100;
            } else {
                priceOptionsBase = parseFloat(item.properties[CartItemProperties.PRICE]) / 100;
            }
            priceOptions = currencyConvert(priceOptionsBase);
        }

        // show extra add-on product if enabled
        if(ThemeCurrency.checkout.variantId){
            // only add if not zero
            if(priceOptions > 0){
                // insert below product
                bundleProducts.splice(1, 0, {
                    handle: ThemeCurrency.priceProductHandle || 'product-options', // lookup
                    id: ThemeCurrency.checkout.variantId,
                    quantity: mainProduct.quantity,
                    price: priceOptionsBase // converted again below
                });
            }

            priceProduct = true; // avoid adding to main product
        }

        let productPromises = [];

        for (let bundleProduct of bundleProducts) {
            let handle = bundleProduct.handle;
            let variantId = bundleProduct.id;

            // main product use from item
            if(bundleProduct.id === '0'){
                handle = item.handle;
                variantId = item.variant_id;
            }

            // load product and find matching variant
            productPromises.push(this.shopifyStore.getProduct(handle).then(product => {

                // find variant
                let variant = product.variants.find(variant => variant.id === Number(variantId));

                if(!variant){
                    return null;
                }

                // extend bundle product
                bundleProduct = {
                    ... bundleProduct,
                    title: variant.name,
                    img: cdnImg(variant.featured_image?.src || product.featured_image, '200x'),
                    url: `${product.url}?variant=${variant.id}`,
                    // price before discount
                    priceBefore: variant.price / 100
                };

                // currency convert
                if(bundleProduct.price){

                    let bundlePrice = bundleProduct.price;

                    // only set if bundle price is not already in base price
                    if (bundleProduct.priceBase) {
                        bundlePrice = bundleProduct.priceBase;
                    }

                    // always convert to presentment currency (skipped if currencyRate = 1)
                    bundleProduct.price = currencyConvert(bundlePrice);
                }

                // main product price
                if(bundleProduct.id === '0'){

                    // default price (no discount)
                    if(!bundleProduct.price){
                        bundleProduct.price = bundleProduct.priceBefore;
                    }
                    // price update directly on product
                    if(!priceProduct){
                        bundleProduct.price += priceOptions;
                        bundleProduct.priceBefore += priceOptions;
                    }
                    // add-on price with discount
                    else if(bundleProduct.price < 0){

                        // INFO: same logic as in Shopify function
                        let basePrice = bundleProduct.priceBefore;

                        // IMPORTANT: because the bundle price is already negative the TOTAL discount includes the base price (happens for very low base prices + lots of add-ons)
                        let totalDiscount = basePrice + Math.abs(bundleProduct.price);

                        // deduct from options price
                        priceOptions = priceOptions - totalDiscount;
                        // reset main product to full price
                        bundleProduct.price = basePrice;
                    }
                }


                return bundleProduct;
            }));
        }

        bundleProducts = await Promise.all(productPromises);
        // remove null (not found)
        bundleProducts = bundleProducts.filter(p => p);

        // add-on product - update price again (required to apply discounted add-on price) - NOTE: because the products are fetched with promises the execution order is not fixed
        if(priceProduct){
            let addonProduct = bundleProducts.find(p => p.id === ThemeCurrency.checkout.variantId);

            // set add-on price
            if(addonProduct){
                let priceBefore = addonProduct.price;
                addonProduct.price = priceOptions;
                addonProduct.priceBefore = priceBefore;
            }
        }

        // calc total quantity (same as shopify checkout)
        let totalQty = bundleProducts.reduce((total, bundleProduct) => total + (bundleProduct.quantity * item.quantity), 0);

        // default close <script>window.clBundleCartOpen=false;</script>
        render(bundleCartTemplate(item, bundleProducts, totalQty, window.clBundleCartOpen !== false), bundleItemEl);
    }

    _watchQuickView(){

        // quick view click listeners                                // Dawn theme hard coded selector
        let quickViewSelectorList = ['[data-live-quick-view]', '.quick-add__submit'];

        let quickViewSelectors = quickViewSelectorList.join(',');

        // GLOBAL: watch for quick view clicks (we have to use this approach as themes can dynamically add/remove quick view elements)
        document.addEventListener('click', (e) => {
            if(e.target){
                if(Util.matchesIncludeParent(e.target, quickViewSelectors)){

                    // hacky timeout  to wait for quick view to open render
                    // INFO: to make this more robuts on a specific theme - it's best to integrate into the theme code and execute the logic below after quick view is opened
                    setTimeout(() => {

                        // IMPORTANT: reset products to avoid re-init of a previous shown quick view product
                        // INFO: this also re-inits the main product in case of a product page having quick add options below
                        // (e.g Dawn complementary products) - can cause issues when the live preview already replaced the preview image (not worth solving at the moment)
                        const resetProducts = true;

                        this.setup(resetProducts);

                    }, 1000);
                }
            }
        }, { capture: true });
    }

    _compatible(){
        const appMsg = (app) => `[Live Product Options] found incompatible app: ${app} \n**Please reach out to our app support to check your theme**`;
        // show message based on theme editor or not
        const show = (app) => this.shopifyStore.isThemeEditor() ? alert(appMsg(app)) : console.error(appMsg(app));

        if(typeof cst_money_formate !== 'undefined'){ // global const (no window)
            show('Customify');
        } else if(document.querySelector('script[src*="options.ymq"]') !== null){
            show('YMQ Product Options');
        } else if(document.querySelector('script[src*="customily"]') !== null){
            show('Customily');
        }
    }

}

export default OptionsApp;
